import React from 'react';
import { UserGroupIcon, UsersIcon, ShieldCheckIcon, BriefcaseIcon, LockClosedIcon, CogIcon } from '@heroicons/react/24/outline';

export interface HeroBannerProps {
    title: string;
    description: string;
    icon: React.ReactNode;
    variant?: 'teams' | 'roles' | 'vault' | 'settings';
    backButton?: {
        onClick: () => void
        icon: React.ReactNode;
    };
    children?: React.ReactNode; // For tab navigation or other content
    rightContent?: React.ReactNode; // For action buttons
}

const HeroBanner: React.FC<HeroBannerProps> = ({
    title,
    description,
    icon,
    variant = 'teams',
    backButton,
    children,
    rightContent
}) => {
    // Different background patterns for different page types
    const getBackgroundPattern = () => {
        switch (variant) {
            case 'teams':
                return (
                    <>
                        {/* Team Collaboration Network */}
                        <div className="absolute top-6 right-8 md:right-16">
                            <div className="relative">
                                {/* Central hub */}
                                <div className="w-16 h-16 bg-white/30 rounded-full border-2 border-white/60 flex items-center justify-center backdrop-blur-sm shadow-lg">
                                    <UserGroupIcon className="w-8 h-8 text-white drop-shadow-lg" />
                                </div>
                                {/* Connected nodes */}
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-white/40 rounded-full border border-white/70 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-2 bg-white rounded-full drop-shadow-sm"></div>
                                </div>
                                <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-white/40 rounded-full border border-white/70 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-2 bg-white rounded-full drop-shadow-sm"></div>
                                </div>
                                <div className="absolute top-4 -left-8 w-6 h-6 bg-white/35 rounded-full border border-white/60 shadow-sm"></div>
                                {/* Connection lines */}
                                <div className="absolute top-2 right-6 w-8 h-0.5 bg-white/50 rotate-45 drop-shadow-sm"></div>
                                <div className="absolute bottom-2 left-6 w-8 h-0.5 bg-white/50 -rotate-45 drop-shadow-sm"></div>
                            </div>
                        </div>
                        
                        {/* Organizational Structure */}
                        <div className="absolute bottom-6 left-8 md:left-16">
                            <div className="space-y-3">
                                {/* Management Level */}
                                <div className="flex justify-center">
                                    <div className="w-12 h-8 bg-white/40 rounded-lg border border-white/60 flex items-center justify-center shadow-md">
                                        <div className="w-6 h-2 bg-white/80 rounded drop-shadow-sm"></div>
                                    </div>
                                </div>
                                {/* Team Level */}
                                <div className="flex space-x-2">
                                    <div className="w-8 h-6 bg-white/35 rounded border border-white/55 flex items-center justify-center shadow-sm">
                                        <div className="w-4 h-1.5 bg-white/70 rounded drop-shadow-sm"></div>
                                    </div>
                                    <div className="w-8 h-6 bg-white/35 rounded border border-white/55 flex items-center justify-center shadow-sm">
                                        <div className="w-4 h-1.5 bg-white/70 rounded drop-shadow-sm"></div>
                                    </div>
                                    <div className="w-8 h-6 bg-white/35 rounded border border-white/55 flex items-center justify-center shadow-sm">
                                        <div className="w-4 h-1.5 bg-white/70 rounded drop-shadow-sm"></div>
                                    </div>
                                </div>
                                {/* Connection lines */}
                                <div className="flex justify-center">
                                    <div className="w-16 h-0.5 bg-white/40 drop-shadow-sm"></div>
                                </div>
                            </div>
                        </div>

                        {/* Floating Productivity Icons */}
                        <div className="absolute top-1/3 left-1/4 animate-pulse">
                            <div className="w-10 h-10 bg-white/25 rounded-xl border border-white/45 flex items-center justify-center backdrop-blur-sm shadow-lg">
                                <BriefcaseIcon className="w-5 h-5 text-white/90 drop-shadow-md" />
                            </div>
                        </div>
                        
                        {/* Success Metrics */}
                        <div className="absolute top-12 left-12 hidden lg:block">
                            <div className="space-y-1">
                                <div className="w-20 h-2 bg-white/30 rounded-full overflow-hidden shadow-sm">
                                    <div className="w-16 h-full bg-white/60 rounded-full drop-shadow-sm"></div>
                                </div>
                                <div className="w-16 h-2 bg-white/25 rounded-full overflow-hidden shadow-sm">
                                    <div className="w-12 h-full bg-white/50 rounded-full drop-shadow-sm"></div>
                                </div>
                                <div className="w-12 h-2 bg-white/20 rounded-full overflow-hidden shadow-sm">
                                    <div className="w-8 h-full bg-white/40 rounded-full drop-shadow-sm"></div>
                                </div>
                            </div>
                        </div>
                    </>
                );

            case 'roles':
                return (
                    <>
                        {/* Security Shield Matrix */}
                        <div className="absolute top-8 right-12 md:right-20">
                            <div className="relative">
                                {/* Main shield */}
                                <div className="w-16 h-20 bg-white/35 rounded-lg border-2 border-white/60 flex items-center justify-center backdrop-blur-sm shadow-lg">
                                    <ShieldCheckIcon className="w-8 h-8 text-white drop-shadow-lg" />
                                </div>
                                {/* Permission layers */}
                                <div className="absolute -right-2 top-2 w-6 h-8 bg-white/30 rounded border border-white/55 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-4 bg-white/60 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="absolute -left-2 top-4 w-6 h-8 bg-white/25 rounded border border-white/50 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-4 bg-white/50 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="absolute -right-1 bottom-2 w-4 h-6 bg-white/20 rounded border border-white/45 flex items-center justify-center shadow-md">
                                    <div className="w-1 h-3 bg-white/45 rounded drop-shadow-sm"></div>
                                </div>
                            </div>
                        </div>

                        {/* Permission Matrix Grid */}
                        <div className="absolute bottom-8 left-12 md:left-20">
                            <div className="grid grid-cols-5 gap-1">
                                {[...Array(15)].map((_, i) => (
                                    <div 
                                        key={i} 
                                        className={`w-3 h-3 rounded-sm border shadow-sm ${
                                            i < 8 
                                                ? 'bg-green-400/60 border-green-300/50' 
                                                : i < 12 
                                                ? 'bg-yellow-400/50 border-yellow-300/45' 
                                                : 'bg-gray-400/40 border-gray-300/40'
                                        }`}
                                    ></div>
                                ))}
                            </div>
                            <div className="mt-2 text-xs text-white/60 drop-shadow-sm">Access Matrix</div>
                        </div>

                        {/* Role Hierarchy Visual */}
                        <div className="absolute top-1/4 left-1/5 hidden lg:block">
                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <div className="w-8 h-8 bg-white/35 rounded-full border border-white/60 flex items-center justify-center shadow-md">
                                        <div className="w-3 h-3 bg-white/70 rounded-full drop-shadow-sm"></div>
                                    </div>
                                    <div className="w-12 h-1 bg-white/40 drop-shadow-sm"></div>
                                    <div className="w-6 h-6 bg-white/30 rounded border border-white/50 shadow-sm"></div>
                                </div>
                                <div className="flex items-center space-x-2 ml-4">
                                    <div className="w-6 h-6 bg-white/30 rounded-full border border-white/55 flex items-center justify-center shadow-sm">
                                        <div className="w-2 h-2 bg-white/60 rounded-full drop-shadow-sm"></div>
                                    </div>
                                    <div className="w-8 h-1 bg-white/25 drop-shadow-sm"></div>
                                    <div className="w-4 h-4 bg-white/25 rounded border border-white/45 shadow-sm"></div>
                                </div>
                            </div>
                        </div>

                        {/* Access Level Indicators */}
                        <div className="absolute top-12 left-8 hidden md:block">
                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-red-400 rounded-full drop-shadow-md"></div>
                                    <div className="w-8 h-1 bg-white/50 rounded drop-shadow-sm"></div>
                                    <span className="text-xs text-white/70 drop-shadow-sm">Full</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-yellow-400 rounded-full drop-shadow-md"></div>
                                    <div className="w-6 h-1 bg-white/45 rounded drop-shadow-sm"></div>
                                    <span className="text-xs text-white/70 drop-shadow-sm">Limited</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-green-400 rounded-full drop-shadow-md"></div>
                                    <div className="w-4 h-1 bg-white/40 rounded drop-shadow-sm"></div>
                                    <span className="text-xs text-white/70 drop-shadow-sm">Read</span>
                                </div>
                            </div>
                        </div>
                    </>
                );

            case 'vault':
                return (
                    <>
                        {/* Secure Vault Visual */}
                        <div className="absolute top-8 right-12 md:right-20">
                            <div className="relative">
                                {/* Main vault */}
                                <div className="w-16 h-16 bg-white/30 rounded-xl border-2 border-white/60 flex items-center justify-center backdrop-blur-sm shadow-lg">
                                    <LockClosedIcon className="w-8 h-8 text-white drop-shadow-lg" />
                                </div>
                                {/* Security layers */}
                                <div className="absolute -top-2 -right-2 w-6 h-6 bg-white/35 rounded-lg border border-white/60 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-2 bg-green-400 rounded-full drop-shadow-md"></div>
                                </div>
                                <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-white/35 rounded-lg border border-white/60 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full drop-shadow-md"></div>
                                </div>
                                {/* Encryption rings */}
                                <div className="absolute -inset-4 border border-white/20 rounded-full drop-shadow-sm"></div>
                                <div className="absolute -inset-6 border border-white/10 rounded-full drop-shadow-sm"></div>
                            </div>
                        </div>

                        {/* Data Categories */}
                        <div className="absolute bottom-8 left-12 md:left-20">
                            <div className="grid grid-cols-3 gap-2">
                                <div className="w-6 h-8 bg-blue-400/50 rounded border border-blue-300/60 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-4 bg-blue-400/80 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="w-6 h-8 bg-green-400/50 rounded border border-green-300/60 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-4 bg-green-400/80 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="w-6 h-8 bg-purple-400/50 rounded border border-purple-300/60 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-4 bg-purple-400/80 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="w-6 h-8 bg-yellow-400/50 rounded border border-yellow-300/60 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-4 bg-yellow-400/80 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="w-6 h-8 bg-red-400/50 rounded border border-red-300/60 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-4 bg-red-400/80 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="w-6 h-8 bg-gray-400/50 rounded border border-gray-300/60 flex items-center justify-center shadow-md">
                                    <div className="w-2 h-4 bg-gray-400/80 rounded drop-shadow-sm"></div>
                                </div>
                            </div>
                            <div className="mt-2 text-xs text-white/60 drop-shadow-sm">Secure Storage</div>
                        </div>

                        {/* Security Status */}
                        <div className="absolute top-1/3 left-1/4 hidden lg:block">
                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse drop-shadow-md"></div>
                                    <span className="text-xs text-white/80 drop-shadow-sm">Encrypted</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse drop-shadow-md"></div>
                                    <span className="text-xs text-white/80 drop-shadow-sm">Backed Up</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse drop-shadow-md"></div>
                                    <span className="text-xs text-white/80 drop-shadow-sm">Synced</span>
                                </div>
                            </div>
                        </div>
                    </>
                );

            case 'settings':
                return (
                    <>
                        {/* Configuration Hub */}
                        <div className="absolute top-8 right-12 md:right-20">
                            <div className="relative">
                                {/* Main gear */}
                                <div className="w-16 h-16 bg-white/30 rounded-full border-2 border-white/60 flex items-center justify-center backdrop-blur-sm animate-spin-slow shadow-lg">
                                    <CogIcon className="w-8 h-8 text-white drop-shadow-lg" />
                                </div>
                                {/* Connected gears */}
                                <div className="absolute -top-2 -right-6 w-8 h-8 bg-white/35 rounded-full border border-white/60 flex items-center justify-center animate-reverse-spin shadow-md">
                                    <CogIcon className="w-4 h-4 text-white drop-shadow-md" />
                                </div>
                                <div className="absolute -bottom-4 -left-4 w-10 h-10 bg-white/25 rounded-full border border-white/50 flex items-center justify-center animate-spin-slow shadow-md">
                                    <CogIcon className="w-5 h-5 text-white drop-shadow-md" />
                                </div>
                            </div>
                        </div>

                        {/* System Status */}
                        <div className="absolute bottom-8 left-12 md:left-20">
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                    <div className="w-16 h-2 bg-white/30 rounded-full overflow-hidden shadow-sm">
                                        <div className="w-14 h-full bg-green-400/80 rounded-full drop-shadow-sm"></div>
                                    </div>
                                    <span className="text-xs text-white/70 drop-shadow-sm">System Health</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="w-16 h-2 bg-white/30 rounded-full overflow-hidden shadow-sm">
                                        <div className="w-12 h-full bg-blue-400/80 rounded-full drop-shadow-sm"></div>
                                    </div>
                                    <span className="text-xs text-white/70 drop-shadow-sm">Performance</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="w-16 h-2 bg-white/30 rounded-full overflow-hidden shadow-sm">
                                        <div className="w-10 h-full bg-yellow-400/80 rounded-full drop-shadow-sm"></div>
                                    </div>
                                    <span className="text-xs text-white/70 drop-shadow-sm">Storage</span>
                                </div>
                            </div>
                        </div>

                        {/* Configuration Blocks */}
                        <div className="absolute top-1/4 left-1/5 hidden lg:block">
                            <div className="grid grid-cols-2 gap-2">
                                <div className="w-8 h-6 bg-white/30 rounded border border-white/50 flex items-center justify-center shadow-sm">
                                    <div className="w-4 h-1 bg-white/60 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="w-8 h-6 bg-white/25 rounded border border-white/45 flex items-center justify-center shadow-sm">
                                    <div className="w-4 h-1 bg-white/50 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="w-8 h-6 bg-white/25 rounded border border-white/45 flex items-center justify-center shadow-sm">
                                    <div className="w-4 h-1 bg-white/50 rounded drop-shadow-sm"></div>
                                </div>
                                <div className="w-8 h-6 bg-white/20 rounded border border-white/40 flex items-center justify-center shadow-sm">
                                    <div className="w-4 h-1 bg-white/45 rounded drop-shadow-sm"></div>
                                </div>
                            </div>
                        </div>
                    </>
                );

            default:
                return (
                    <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-white/20 rounded-full border border-white/40 shadow-lg backdrop-blur-sm"></div>
                );
        }
    };

    return (
        <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
                {getBackgroundPattern()}
            </div>
            
            <div className="relative px-6 py-12">
                <div className="max-w-7xl mx-auto">
                    {/* Header Section */}
                    <div className="flex justify-between items-start mb-8">
                        <div className="flex items-center space-x-4">
                            {backButton && (
                                <button
                                    onClick={backButton.onClick}
                                    className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg hover:bg-white/30 transition-all duration-200"
                                >
                                    {backButton.icon}
                                </button>
                            )}
                            <div className="flex items-center space-x-3">
                                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg">
                                    {icon}
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-white">
                                        {title}
                                    </h1>
                                    <p className="text-white/70 text-sm">
                                        {description}
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        {rightContent && (
                            <div className="flex items-center space-x-3">
                                {rightContent}
                            </div>
                        )}
                    </div>

                    {/* Tab Navigation or Custom Content */}
                    {children}
                </div>
            </div>
        </div>
    );
};

export default HeroBanner;
